/** 계정 창고 — 물건마다 가방·창고 수량과 이동 방향을 함께 본다. */
import { S } from '../../core/state.js';
import { POTIONS } from '../../world/map.js';
import { buyBtn, eqName, gearEdge, junkName, lore, muted, npcHead, num, sec, split } from './common.js';
import { equipmentComparison } from '../equipment-compare.js';
import { guildShopStatus } from './guild-shop.js';
import { carryHtml, vaultEndHtml, vaultFateText } from '../world-end-notice.js';

export function render({ y, at, eq, j }) {
  const v = S.vault;
  const carry = carryHtml(y);
  let h = guildShopStatus() + npcHead()
    + `<div class="vault-end">${muted(vaultEndHtml())}${carry ? `<details class="vault-carry" id="vault-carry"><summary>새 세계 창고에 들어가는 것</summary>${muted(carry)}</details>` : ''}</div>`;
  if (!v) return h + muted(S.loadErr ? S.loadErr + ' — 패널을 다시 열면 재시도한다.' : '불러오는 중…');

  h += sec('은화 옮기기', `<div class="ux-facts"><div class="ux-fact"><small>가방 은화</small><b class="ux-number">${num(y.silver)}</b></div>`
    + `<div class="ux-fact"><small>창고 은화</small><b class="ux-number">${num(v.silver)}</b></div></div>`
    + `<label class="row" for="vsilver">옮길 금액 <input id="vsilver" type="number" inputmode="numeric" min="1" placeholder="은화" style="flex:1;min-width:0;padding:6px 8px"></label>`
    + `<div class="ux-actions">${buyBtn('가방 → 창고', 'act.vaultSilver(1)', { at, need: 'vault', more: y.silver > 0 ? [] : ['가진 은화 없음'] })}`
    + `${buyBtn('창고 → 가방', 'act.vaultSilver(0)', { at, need: 'unvault', more: v.silver > 0 ? [] : ['맡긴 은화 없음'] })}</div>`
    + `<div class="row">${buyBtn(`전부 맡기기 ${num(y.silver)}`, 'act.vaultSilver(1,-1)', { at, need: 'vault', more: y.silver > 0 ? [] : ['가진 은화 없음'] })}`
    + `${buyBtn(`전부 찾기 ${num(v.silver)}`, 'act.vaultSilver(0,-1)', { at, need: 'unvault', more: v.silver > 0 ? [] : ['맡긴 은화 없음'] })}</div>`
    + muted('마을에서 구매·강화할 때 가방 은화가 부족하면 창고에서 바로 낸다.'));

  const ve = v.equips || [];
  const boundPending = (e) => e.guildBound != null && (S.guildShopPending || S.guildMoneyPending) ? ['앞선 처리 확인 중'] : [];
  const selected = eq.find((e) => e.id === S.gearWorkId) || ve.find((e) => e.id === S.gearWorkId);
  const inBag = selected && eq.some((e) => e.id === selected.id);
  const moveEquip = (e, into) => buyBtn(into ? '이 장비 맡기기' : '이 장비 꺼내기',
    `act.${into ? 'vaultEquip' : 'unvaultEquip'}('${e.id}')`, { at, need: into ? 'vault' : 'unvault', more: boundPending(e) });
  const selectedFocus = selected ? '<button class="ghost sheetx" onclick="act.gearWorkClear()">닫기</button>'
    + `<div class="fname"><b>${eqName(selected)}</b><span class="qt">${inBag ? '가방 → 창고' : '창고 → 가방'}</span></div>`
    + equipmentComparison(selected, y)
    + `<div class="ux-actions">${moveEquip(selected, inBag)}`
    + (inBag ? `<button class="ghost" onclick="act.gearWork('${selected.id}','inv')">가방에서 비교·착용</button>` : '')
    + `</div>${inBag && Object.values(y.inv.equipped || {}).includes(selected.id) ? muted('착용 중인 장비다. 맡기면 착용이 해제된다.') : ''}` : '';
  for (const [into, pool, title] of [[1, eq, '가방 장비 · 맡기기'], [0, ve, '창고 장비 · 꺼내기']]) {
    h += sec(`${title} <em>${pool.length}</em>`, pool.length ? pool.map((e) => `<div class="item gd${e.id === selected?.id ? ' sel' : ''}" ${gearEdge(e)}>`
      + `<span class="nm">${eqName(e)}${e.id === selected?.id ? '<span class="qt">선택한 장비</span>' : ''}${into && Object.values(y.inv.equipped || {}).includes(e.id) ? '<span class="qt">착용 중 · 맡기면 해제</span>' : ''}</span>`
      + `<button class="ghost" onclick="act.gearWork('${e.id}','vault')">살펴보기</button>${moveEquip(e, into)}</div>`).join('') : muted(into ? '맡길 장비가 없다.' : '보관된 장비가 없다.'));
  }

  const transfers = (key, n, into) => {
    if (!(n > 0)) return '';
    const word = into ? '맡기기' : '찾기';
    const quantities = [...new Set([1, Math.min(100, n), n])];
    return `<div class="row"><span class="qt">${into ? '가방 → 창고' : '창고 → 가방'}</span>`
      + quantities.map((qty) => buyBtn(`${word} ${qty === n && n > 1 ? `전부 ${num(qty)}개` : `${num(qty)}개`}`,
        `act.vaultJunk('${key}',${into},${qty})`, { at, need: into ? 'vault' : 'unvault' })).join('') + '</div>';
  };
  // 같은 물건의 양쪽 수량과 이동을 한 자리에 둔다. 보관 전후 다른 목록을 다시 찾지 않는다.
  const pairedStock = (bag, stored, name, allowed = () => true) => [...new Set([...Object.keys(bag || {}), ...Object.keys(stored || {})])]
    .filter((k) => allowed(k) && ((bag?.[k] || 0) > 0 || (stored?.[k] || 0) > 0))
    .map((k) => `<div class="ux-card"><div class="kv"><b>${name(k)}</b><span>가방 ${num(bag?.[k] || 0)} · 창고 ${num(stored?.[k] || 0)}</span></div>`
      + transfers(k, bag?.[k] || 0, 1) + transfers(k, stored?.[k] || 0, 0) + '</div>').join('');
  h += sec('재화 옮기기', muted('가방 속 잡템은 사망하면 전부 떨어진다. 맡긴 재화는 사망해도 남는다.')
    + (pairedStock(j, v.junk, junkName) || muted('가방과 창고에 재화가 없다.')));
  const consumName = (k) => POTIONS.find((s) => s.id === k)?.name ?? k;
  h += sec('소모품 옮기기', pairedStock(y.inv.consum, v.consum, consumName, (k) => POTIONS.some((s) => s.id === k)) || muted('가방과 창고에 소모품이 없다.'));
  h += lore('창고 보관과 세계 종료', `같은 계정의 캐릭터가 공유한다. 보관한 물품은 사망해도 잃지 않는다.<br>`
    + '사망하면 가방의 잡템 전부와 소지 은화 절반이 드롭된다. 장비도 확률에 따라 드롭된다.<br>'
    + `마을 안에서는 맡긴 은화·강화석을 바로 쓴다. 거래에 올릴 은화는 가방에 있어야 한다.<br><b>${vaultFateText(y)}.</b>`);
  return split('', h, selectedFocus);
}
